import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';
import { fromJS } from 'immutable';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';

import { getPopupRegistration, openPopup, Priority } from 'app/PopupHolder';
import Modal from 'components/base/Modal';
import { selectLoading } from 'data/property';
import { clearAll, loadSearch, selectFavoriteSearches, FAVORITE_SEARCH_SLOTS, setFavoriteItem, setSuggestionLabel } from 'data/search';

import getFields, { getDisplayFields } from './Constants';
import css from './ViewerNew.scss';


const isNumberType = type => ['number', 'percent', 'decimal', 'currency'].includes(type);

// Renders a single min/max value with the range-suffix classes. The suffix text
// (e.g. "or more", "to") is supplied via CSS :after in ViewerNew.scss, mirroring
// the logic that lives in Constants.writeField for the legacy viewer.
const renderFieldValue = (field, max = false) => {
  const { ignore, range, value = null, valueMin = null, valueMax = null, type, name } = field;
  if (ignore || (max && valueMax == null) || (!max && (value || valueMin) == null)) return null;

  const fullRange = valueMin != null && valueMax != null;
  const year = name.toLowerCase().includes('year');

  const classes = classNames(css.fieldValue, {
    [css.rangeValue]: range && (!max || !fullRange),
    [css.fullRange]: fullRange,
    [css.minValue]: !max,
    [css.maxValue]: max,
    [css.numberValue]: !year && isNumberType(type),
    [css.quantityValue]: name === 'bathrooms' || (type === 'number' && !name.endsWith('Feet')),
    [css.dateValue]: year || type === 'date',
  });

  return <div className={classes}>{max ? valueMax : valueMin || value}</div>;
};

class SearchFilterViewerNew extends PureComponent {
  constructor(props) {
    super(props);

    this.handleSearch = this.handleSearch.bind(this);

    const { availableListingTypes } = this.props;
    const editSearch = this.props.search || {};

    const { formattedAddress, estimatedValueGrowthPeriod = 'ONE_MONTH' } = editSearch;
    const search = fromJS(editSearch).set('estimatedValueGrowthPeriod', estimatedValueGrowthPeriod);
    const fields = getFields(search, availableListingTypes).fields;

    if (formattedAddress) fields.unshift({ name: 'location', label: 'Location', value: formattedAddress });

    this.state = {
      fields,
      search,
      searchEnabled: !!getDisplayFields(fields).length,
    };
  }

  handleSearch() {
    const { closePopup, clearAll, loadSearch, favoriteSearches, history } = this.props;

    closePopup();
    history.push('/search');
    clearAll();
    loadSearch(this.state.search.toJS());
    const currentSearch = this.state.search.toJS();
    const currentListingType = currentSearch.listingType;

    // Compare with favoriteSearches
    const FavoriteSlots = Array.from({ length: FAVORITE_SEARCH_SLOTS }, (v, i) => i + 1);

    let matchedFavorite = null;

    FavoriteSlots.forEach((idx) => {
      const fav = favoriteSearches.get(String(idx));
      if (fav && fav.get('listingType') === currentListingType) {
        matchedFavorite = fav.toJS();
      }
    });

    if (matchedFavorite) {
      this.props.setFavoriteItem(matchedFavorite);
      this.props.setSuggestionLabel(this.state.search.toJS()?.formattedAddress);
    }
  }

  renderFields() {
    const { fields } = this.state;
    const displayFields = getDisplayFields(fields);

    if (!displayFields.length) {
      return <div className={css.noCriteria}>No Additional Criteria Entered</div>;
    }

    const growthPeriod = fields.find(f => f.name === 'estimatedValueGrowthPeriod');
    const valueDescClass = classNames(css.fieldValue, css.valueDesc);

    return (
      <div className={css.filters}>
        {displayFields.map(field => (
          <div className={css.filterRow} key={field.name}>
            <div className={css.filterLabel}>
              {field.label}
              {field.includeKey ? (!field.include ? ' (Exclude)' : ' (Include)') : ''}
              {field.value ? ':' : ''}
            </div>
            <div className={css.filterValues}>
              {renderFieldValue(field)}
              {renderFieldValue(field, true)}
              {field.name !== 'estimatedValueGrowth' || !(growthPeriod && growthPeriod.value) ? null : (
                <div className={valueDescClass}>over {String(growthPeriod.value).toLowerCase()}</div>
              )}
              {field.name !== 'mortgageTerm' ? null : (
                <div className={valueDescClass}>{`month${field.valueMin != null && Number(field.valueMax) === 1 ? '' : 's'}`}</div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  render() {
    const { closePopup, loading } = this.props;
    const { searchEnabled } = this.state;

    return (
      <Modal isOpen uniqId="SearchFilterViewerPopupNew" padding="0" width="auto" onClose={closePopup}>
        <div className={css.card}>
          <div className={css.content}>
            <div className={css.title}>Current List Search Filters</div>
            {this.renderFields()}
            <div className={css.buttons}>
              <button type="button" className={css.btnSecondary} disabled={loading} onClick={closePopup}>Close</button>
              {!searchEnabled ? null : (
                <button type="button" className={css.btnPrimary} disabled={loading} onClick={this.handleSearch}>Rerun Search</button>
              )}
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

SearchFilterViewerNew.propTypes = {
  search: PropTypes.object,
  availableListingTypes: PropTypes.array,
  loading: PropTypes.bool,
  closePopup: PropTypes.func.isRequired,
  favoriteSearches: PropTypes.object,
  clearAll: PropTypes.func,
  loadSearch: PropTypes.func,
  setFavoriteItem: PropTypes.func,
  setSuggestionLabel: PropTypes.func,
  history: PropTypes.object,
};

const SearchFilterViewerPopupNew = withRouter(connect(state => ({
  loading: selectLoading(state),
  favoriteSearches: selectFavoriteSearches(state),
}), {
  clearAll,
  loadSearch,
  setFavoriteItem,
  setSuggestionLabel,
})(SearchFilterViewerNew));

const registrationId = getPopupRegistration(SearchFilterViewerPopupNew);
SearchFilterViewerPopupNew.open = props => openPopup(registrationId, { ...props, priority: Priority.MEDIUM });

export default SearchFilterViewerPopupNew;
